import { Injectable, inject } from '@angular/core';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { findNavMatchByRoute } from '../config/navigation.config';
import { PageTitleService } from './page-title.service';

/**
 * Sets the browser-tab title on every navigation, for every route in the app. Registered against
 * `TitleStrategy` in `app.config.ts`; nothing else needs wiring.
 *
 * Resolution order — first hit wins:
 *
 * 1. **`title` on the route.** Angular walks root → leaf and keeps the deepest one, so a child
 *    overrides its parent and a parent covers all of its children. Used for the screens that have
 *    no sidebar entry: auth, kiosk, dashboard, the public shell, the full-screen document runners.
 * 2. **The navigation config** — the longest-matching nav leaf's `label`, exactly as
 *    `buildPageHeaderFromRoute` and the breadcrumbs resolve it. Every sidebar screen is therefore
 *    titled with the same string the user clicked to get there, and a detail route inherits its
 *    list's title (`/company/users/:id` → "Users") with no per-route wiring.
 * 3. **Nothing matched** — the app name on its own.
 *
 * Add a screen to the sidebar and its title comes for free. A page that can do better than a
 * static label sets the record's own name via `PageTitleService`.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private readonly pageTitle = inject(PageTitleService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    // `EncryptedUrlSerializer` puts the query string back as `?q=<cipher>` — match on the path only.
    const path = snapshot.url.split('?')[0].split('#')[0];
    this.pageTitle.setTitle(this.buildTitle(snapshot) ?? findNavMatchByRoute(path)?.leaf.label);
  }
}
